/**
 * Dev/audit-only loading instrumentation — VERIFY-07 + coordinator phaseCountByEye.
 * Does not change loading behavior; samples visibility and stops automatically.
 */

import {
  computePhaseCountByEye,
  getCoordinatorVisibleState,
  getLoadingAuditEvents,
  getLoadingCoordinatorSnapshot,
} from '@/lib/loading/loadingCoordinatorStore';
import type { LoadingAuditEvent } from '@/lib/loading/types';

const BOOT_T0_KEY = '__loading_boot_t0_samples__';
const TRANSITION_KEY = '__loading_transition_timeline__';
const AUDIT_FLAG_KEY = 'loading_audit_enabled';

export type LoadingAuditSample = {
  t: number;
  path: string;
  preReact: boolean;
  bootstrapSplash: boolean;
  pageLoaderDom: number;
  pageLoaderVisible: number;
  routeChunkDom: number;
  routeChunkVisible: number;
  routeSuppressed: number;
  dashboardSuppressed: boolean;
  configured: number;
  scopes: string[];
  typeMs: Array<{ type: string | null; ms: string | null; source: string | null }>;
  texts: string[];
  visibleOwners: string[];
  coordinatorSurface: string;
  coordinatorOwner: string;
  coordinatorSource: string;
  phaseCountByEye: number;
};

function auditEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (/[?&]loadingAudit=1(?:&|$)/.test(window.location.search)) return true;
    if (localStorage.getItem(AUDIT_FLAG_KEY) === '1') return true;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return true;
  } catch {
    // ignore
  }
  return import.meta.env.DEV;
}

export function isLoadingAuditEnabled(): boolean {
  return auditEnabled();
}

export function isElementVisible(el: Element | null | undefined): boolean {
  if (!el || !el.isConnected) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  if (el.classList.contains('sr-only')) return false;
  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  if (parseFloat(style.opacity) === 0) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function collectTypeMs(): LoadingAuditSample['typeMs'] {
  const out: LoadingAuditSample['typeMs'] = [];
  document.querySelectorAll('[data-loading-animation-type]').forEach((el) => {
    if (!isElementVisible(el)) return;
    out.push({
      type: el.getAttribute('data-loading-animation-type'),
      ms: el.getAttribute('data-loading-duration-ms'),
      source: el.getAttribute('data-loading-config-source'),
    });
  });
  return out;
}

function collectScopes(): string[] {
  const scopes = new Set<string>();
  document.querySelectorAll('[data-loading-scope]').forEach((el) => {
    if (!isElementVisible(el)) return;
    const scope = el.getAttribute('data-loading-scope');
    if (scope) scopes.add(scope);
  });
  return [...scopes];
}

function collectTexts(): string[] {
  const body = document.body?.innerText ?? '';
  const texts: string[] = [];
  if (body.includes('Đang đăng nhập')) texts.push('Đang đăng nhập');
  if (body.includes('Đang tải dashboard runtime')) texts.push('Đang tải dashboard runtime');
  if (body.includes('Đang tải nội dung')) texts.push('Đang tải nội dung');
  if (body.includes('Đang khởi tạo')) texts.push('Đang khởi tạo');
  return texts;
}

export function sampleLoadingOwners(tOffset = 0): LoadingAuditSample {
  const preReactEl = document.getElementById('pre-react-boot-splash');
  const bootstrapEl = document.querySelector(
    '[data-bootstrap-splash]:not([data-bootstrap-splash="handoff"]):not(#pre-react-boot-splash)',
  );
  const pageLoaderEls = [...document.querySelectorAll('[data-page-loader]')];
  const routeChunkEls = [
    ...document.querySelectorAll('[data-route-chunk-loader="true"]'),
    ...document.querySelectorAll('[data-loading-coordinator-surface="route"]'),
  ];
  const visibleOwners: string[] = [];

  const preReact = isElementVisible(preReactEl);
  const bootstrapSplash = isElementVisible(bootstrapEl);
  const pageLoaderVisible = pageLoaderEls.filter((el) => isElementVisible(el)).length;
  const routeChunkVisible = routeChunkEls.filter((el) => isElementVisible(el)).length;
  const coord = getCoordinatorVisibleState();

  if (preReact) visibleOwners.push('preReact');
  if (bootstrapSplash) visibleOwners.push('bootstrapSplash');
  if (pageLoaderVisible > 0) visibleOwners.push('pageLoader');
  if (routeChunkVisible > 0) visibleOwners.push('routeChunk');
  if (coord.surface === 'content') visibleOwners.push('coordinator:content');
  if (coord.surface === 'fullscreen') visibleOwners.push('coordinator:fullscreen');

  document.querySelectorAll('[data-configured-loading]').forEach((el) => {
    if (!isElementVisible(el)) return;
    if (el.id === 'pre-react-boot-splash' && preReact) return;
    const scope = el.getAttribute('data-loading-scope') ?? 'configured';
    if (!visibleOwners.includes(`configured:${scope}`)) {
      visibleOwners.push(`configured:${scope}`);
    }
  });

  if (document.querySelector('[data-dashboard-runtime-suppressed]') && collectTexts().includes('Đang tải dashboard runtime')) {
    // suppressed panel should not add visible owner
  } else if (collectTexts().includes('Đang tải dashboard runtime')) {
    visibleOwners.push('dashboardRuntime');
  }

  return {
    t: Math.round(tOffset),
    path: window.location.pathname,
    preReact,
    bootstrapSplash,
    pageLoaderDom: pageLoaderEls.length,
    pageLoaderVisible,
    routeChunkDom: routeChunkEls.length,
    routeChunkVisible,
    routeSuppressed: document.querySelectorAll('[data-route-chunk-loader="suppressed"]').length,
    dashboardSuppressed: !!document.querySelector('[data-dashboard-runtime-suppressed]'),
    configured: [...document.querySelectorAll('[data-configured-loading]')].filter((el) =>
      isElementVisible(el),
    ).length,
    scopes: collectScopes(),
    typeMs: collectTypeMs(),
    texts: collectTexts(),
    visibleOwners,
    coordinatorSurface: coord.surface,
    coordinatorOwner: coord.owner,
    coordinatorSource: coord.activeRequest?.source ?? 'none',
    phaseCountByEye: computePhaseCountByEye(getLoadingAuditEvents()),
  };
}

let transitionSamplerStarted = false;

export function initLoadingTransitionAuditSampler(): void {
  if (!auditEnabled() || transitionSamplerStarted || typeof window === 'undefined') return;
  transitionSamplerStarted = true;
  initLoadingCoordinatorAuditWindowApis();

  const t0 = performance.now();
  const samples: LoadingAuditSample[] = [];
  const push = () => {
    try {
      samples.push(sampleLoadingOwners(Math.round(performance.now() - t0)));
      sessionStorage.setItem(TRANSITION_KEY, JSON.stringify(samples));
      (window as Window & { __loadingTransitionTimeline?: LoadingAuditSample[] }).__loadingTransitionTimeline =
        samples;
    } catch {
      // ignore
    }
  };

  push();
  const iv = window.setInterval(() => {
    push();
    if (performance.now() - t0 >= 15_000) {
      window.clearInterval(iv);
    }
  }, 5);
}

export function getBootT0SamplesFromStorage(): LoadingAuditSample[] {
  try {
    const raw = sessionStorage.getItem(BOOT_T0_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LoadingAuditSample[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function getTransitionTimelineFromStorage(): LoadingAuditSample[] {
  try {
    const raw = sessionStorage.getItem(TRANSITION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LoadingAuditSample[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

let auditWindowApisInstalled = false;

/** Expose coordinator audit APIs on window in dev/audit mode only. */
export function initLoadingCoordinatorAuditWindowApis(): void {
  if (!auditEnabled() || auditWindowApisInstalled || typeof window === 'undefined') return;
  auditWindowApisInstalled = true;

  const w = window as Window & {
    __getLoadingAuditEvents?: () => LoadingAuditEvent[];
    __getLoadingCoordinatorSnapshot?: () => ReturnType<typeof getLoadingCoordinatorSnapshot>;
  };

  w.__getLoadingAuditEvents = () => JSON.parse(JSON.stringify(getLoadingAuditEvents()));
  w.__getLoadingCoordinatorSnapshot = () => JSON.parse(JSON.stringify(getLoadingCoordinatorSnapshot()));
}

const ROUTE_CHUNK_ERROR_KEY = '__route_chunk_error__';

function isChunkLoadErrorMessage(msg: string): boolean {
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Unexpected token <')
  );
}

export type RouteChunkErrorAuditPayload = {
  at: number;
  pathname: string;
  search: string;
  name: string;
  message: string;
  stack: string | null;
  componentStack: string | null;
  isChunkLoadError: boolean;
  coordinatorSnapshot: ReturnType<typeof getLoadingCoordinatorSnapshot>;
  auditEventsLast50: LoadingAuditEvent[];
  loadingStorageKeys: Record<string, string | null>;
};

/** Audit/dev-only: persist boundary error for reverify poll. */
export function captureRouteChunkErrorForAudit(error: Error, errorInfo: ErrorInfo): void {
  if (!auditEnabled() || typeof window === 'undefined') return;

  const msg = error?.message ?? '';
  let loadingStorageKeys: Record<string, string | null> = {};
  try {
    loadingStorageKeys = {
      loading_audit_enabled: localStorage.getItem(AUDIT_FLAG_KEY),
      audit14bAutoPoll: sessionStorage.getItem('__audit14bAutoPoll'),
    };
  } catch {
    // ignore
  }

  const payload: RouteChunkErrorAuditPayload = {
    at: Date.now(),
    pathname: window.location.pathname,
    search: window.location.search,
    name: error.name,
    message: msg,
    stack: error.stack ?? null,
    componentStack: errorInfo.componentStack ?? null,
    isChunkLoadError: isChunkLoadErrorMessage(msg),
    coordinatorSnapshot: getLoadingCoordinatorSnapshot(),
    auditEventsLast50: getLoadingAuditEvents().slice(-50),
    loadingStorageKeys,
  };

  const w = window as Window & { __lastRouteChunkError?: RouteChunkErrorAuditPayload };
  w.__lastRouteChunkError = payload;
  try {
    sessionStorage.setItem(ROUTE_CHUNK_ERROR_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
  console.error('[RouteChunkErrorBoundary:audit]', payload.name, payload.message, payload.componentStack);
}
